import { Doc } from "@/components/Doc/Doc";
import { FlashOnChangeTR } from "@/components/FlashOnChange/FlashOnChange";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { Panel } from "@/components/Panel/Panel";
import { Item } from "@/financeModels/owasco/sourcesAndUses";
import { monospaceFontFamily } from "@/theme";
import { formatDecimalPercent } from "@/utils/format";
import { formatMoney } from "@/utils/moneyFormating";
import { ActionIcon, Group, Table } from "@mantine/core";
import { Atom, useAtomValue } from "jotai";
import { Fragment, useState } from "react";
import { RxTriangleDown, RxTriangleRight } from "react-icons/rx";

export const TotalTable = ({
  label,
  itemsAtom,
  totalAtom,
}: {
  label: string;
  itemsAtom: Atom<Item[]>;
  totalAtom: Atom<number>;
}) => {
  const items = useAtomValue(itemsAtom);
  const totalProjectCost = useAtomValue(totalAtom);

  return (
    <Panel p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{label}</Table.Th>
            <Table.Th ta={"right"} pr={30}>
              Amount
            </Table.Th>
            <Table.Th w={"10%"}></Table.Th>
            <Table.Th ta={"right"} w={90}>
              % of Total
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item) => (
            <ItemRow
              key={item[0]}
              item={item}
              totalProjectCost={totalProjectCost}
            />
          ))}

          <Table.Tr fw={700}>
            <Table.Td>
              <LabelExplainer
                label={"Total Project Cost"}
                explanation={
                  <Doc
                    path={"@/docs/pages/model/overview/sources-total-cost.mdx"}
                  />
                }
              />
            </Table.Td>
            <Table.Td ta={"right"} ff={monospaceFontFamily}>
              {formatMoney(totalProjectCost)}
            </Table.Td>
            <Table.Td></Table.Td>
            <Table.Td ff={monospaceFontFamily} fw={700}>
              100%
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Panel>
  );
};

export const ItemRow = ({
  item,
  totalProjectCost,
  indent = 0,
  isLastChild = false,
}: {
  item: Item;
  totalProjectCost: number;
  indent?: number;
  isLastChild?: boolean;
}) => {
  const [isChildrenOpen, setIsChildrenOpen] = useState(false);
  const hasChildren = item[3] && item[3].length > 0;
  return (
    <Fragment key={item[0]}>
      <FlashOnChangeTR
        key={item[0]}
        content={item?.[1]?.toString() + totalProjectCost?.toString()}
        style={{
          borderBottom:
            (indent > 0 || (hasChildren && isChildrenOpen)) && !isLastChild
              ? "1px solid white"
              : "",
        }}
      >
        <Table.Td pl={indent * 40 + 16}>
          <Group gap={"sm"}>
            {hasChildren && (
              <ActionIcon
                size={"sm"}
                aria-label="Open Children"
                radius={2}
                onClick={() => setIsChildrenOpen((prev) => !prev)}
                variant="light"
                color="accent.4"
                ml={-4}
              >
                {isChildrenOpen ? (
                  <RxTriangleDown size={16} />
                ) : (
                  <RxTriangleRight size={16} />
                )}
              </ActionIcon>
            )}
            <LabelExplainer
              label={item[0]}
              explanation={<Doc path={item[2]} />}
            />
          </Group>
        </Table.Td>
        <Table.Td ta={"right"} ff={monospaceFontFamily}>
          {formatMoney(Number(item[1]))}
        </Table.Td>
        <Table.Td></Table.Td>
        <Table.Td ff={monospaceFontFamily}>
          {formatDecimalPercent(Number(item[1]) / Number(totalProjectCost))}
        </Table.Td>
      </FlashOnChangeTR>
      {isChildrenOpen &&
        item[3] &&
        item[3].map((subItem, i) => (
          <ItemRow
            indent={indent + 1}
            item={subItem}
            totalProjectCost={totalProjectCost}
            isLastChild={i === (item[3] || []).length - 1}
          />
        ))}
    </Fragment>
  );
};
